import classNames from 'classnames';
import styles from './HeroCard.module.scss';
import { UI_Heading } from 'ui/components/1-atoms/Heading/Heading';
import { UI_Picture } from 'ui/components/1-atoms/Media/Picture/Picture';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import ColorOptions, { LinkType } from 'helpers/types';
import { UI_RichText } from 'ui/components/1-atoms/RichText/RichText';
import { UI_Kicker, UI_KickerProps } from 'ui/components/1-atoms/Kicker/Kicker';
import { UI_TopLevelGrid } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGrid';
import { UI_TopLevelGridCell } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGridCell';
import { useEffect, useRef, useState } from 'react';
import { GetImageUrl } from 'helpers/getImageUrl';
import { UI_Player } from 'ui/components/1-atoms/Media/Player/Player';
import TextSplit from 'helpers/textSplit';
import { UI_LinkButton } from 'ui/components/1-atoms/Action/LinkButton/LinkButton';
import { UI_InfoBox, UI_InfoBoxProps } from 'ui/components/2-molecules/InfoBox/InfoBox';

const buttonColorVarMap: Partial<Record<ColorOptions, string>> = {
	[ColorOptions.White]: 'var(--color-white)',
	[ColorOptions.WhiteGrey]: 'var(--color-white-grey)',
	[ColorOptions.LightGrey]: 'var(--color-grey-lightest)',
	[ColorOptions.LightBlue]: 'var(--color-blue-lightest)',
	[ColorOptions.Blue]: 'var(--color-blue)',
	[ColorOptions.Green]: 'var(--color-green)',
	[ColorOptions.Red]: 'var(--color-red)',
	[ColorOptions.Violet]: 'var(--color-violet)',
};

const lightButtonColors = new Set<ColorOptions>([ColorOptions.White, ColorOptions.WhiteGrey, ColorOptions.LightGrey, ColorOptions.LightBlue]);

export interface UI_HeroCardProps {
	className?: string;
	link?: LinkType;
	kicker?: UI_KickerProps;
	kickerLeft?: boolean;
	heading?: string;
	bgColor?: ColorOptions;
	text?: string;
	sideText?: string;
	infoBox?: UI_InfoBoxProps;
	readMoreButtonText?: string;
	readMoreText?: string;
	readMoreButtonSideText?: string;
	readMoreSideText?: string;
	image?: {
		url: string;
		altText: string;
		focalPoint?: { left: number; top: number };
		fileType?: string;
	};
	mobileImage?: {
		url: string;
		altText?: string;
		focalPoint?: { left: number; top: number };
		fileType?: string;
	};
	video?: {
		url: string;
	};
	noOverlay?: boolean;
	imageCropping?: 'cover' | 'contain';
	headingLink?: LinkType;
	linkButtonStyle?: 'primary' | 'secondary';
	linkButtonColor?: ColorOptions;
}

const renderImageOrVideo = (video, image, imageCropping, noOverlay, mobileImage?) => {
	const singleImage = image ?? mobileImage;
	// The video branch renders no <UI_Picture>, so without this the hero would be an
	// empty box until the player mounts and buffers its first frame.
	const videoPoster = singleImage?.url ? GetImageUrl({ url: singleImage.url, width: 1920 }) : undefined;
	return (
		<div className={styles.HeroCard_media}>
			<span className={styles.HeroCard_mediabox}>
				{video?.url ? (
					<UI_Player
						url={video.url}
						poster={videoPoster}
						isLight={false}
						playing={true}
						muted={true}
						loop={true}
						controls={false}
						cover={true}
					/>
				) : mobileImage && image ? (
					<>
						<div className={styles.HeroCard_mobileMedia}>
							<UI_Picture
								aspectRatio={0.5625}
								url={mobileImage.url}
								altText={mobileImage.altText ?? ''}
								focalPoint={mobileImage.focalPoint}
								sizes="100vw"
								imageCropping={imageCropping}
								lazyload={false}
								fetchpriority="high"
								fileType={mobileImage.fileType}
							/>
						</div>
						<div className={styles.HeroCard_desktopMedia}>
							<UI_Picture
								aspectRatio={0.5625}
								url={image.url}
								altText={image.altText}
								focalPoint={image.focalPoint}
								sizes={noOverlay ? '(max-width: 768px) 100vw, 1628px' : '100vw'}
								imageCropping={imageCropping}
								lazyload={false}
								fetchpriority="high"
								fileType={image.fileType}
							/>
						</div>
					</>
				) : singleImage ? (
					<UI_Picture
						aspectRatio={0.5625}
						url={singleImage.url}
						altText={singleImage.altText ?? ''}
						focalPoint={singleImage.focalPoint}
						sizes={noOverlay ? '(max-width: 768px) 100vw, 1628px' : '100vw'}
						imageCropping={imageCropping}
						lazyload={false}
						fetchpriority="high"
						fileType={singleImage.fileType}
					/>
				) : null}

				{!noOverlay && <div className={styles.HeroCard_mediaboxOverlay}></div>}
			</span>
		</div>
	);
};

export const UI_HeroCard: React.FC<UI_HeroCardProps> = ({
	heading,
	link,
	bgColor = 'white',
	kicker,
	text,
	className,
	image,
	mobileImage,
	imageCropping = 'cover',
	sideText,
	infoBox,
	noOverlay,
	kickerLeft,
	video,
	readMoreButtonText,
	readMoreText,
	readMoreButtonSideText,
	readMoreSideText,
	headingLink,
	linkButtonStyle = 'secondary',
	linkButtonColor,
}) => {
	const rootRef = useRef<HTMLElement>(null);

	useEffect(() => {
		if (!rootRef.current) return;

		const textSplit = new TextSplit('.HeroCard_heading', rootRef.current);

		return textSplit.onCleanup.bind(textSplit);
	}, [heading, headingLink, noOverlay]);
	const kickerExists = kicker?.firstWord || kicker?.secondWord;
	const hasImage = image != null || mobileImage != null;
	const buttonColorOverride = linkButtonColor ? buttonColorVarMap[linkButtonColor] : undefined;
	const buttonColorOverrideTextColor = linkButtonColor
		? lightButtonColors.has(linkButtonColor)
			? 'var(--color-black)'
			: 'var(--color-white)'
		: undefined;

	const [scrollPosition, setScrollPosition] = useState(0);
	useEffect(() => {
		const handleScroll = () => {
			setScrollPosition(window.scrollY);
		};
		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	return (
		<header
			ref={rootRef}
			className={classNames(
				styles.HeroCard,
				`u-color___${bgColor}`,
				{ [styles.HeroCard___hasBgImage]: image?.url != null },
				{ [styles.HeroCard___noOverlay]: noOverlay },
				{ [styles.scrolled]: scrollPosition > 0 && !noOverlay },
				className,
			)}
		>
			<div className={styles.HeroCard_container}>
				{(image?.url || mobileImage?.url) && !noOverlay && renderImageOrVideo(video, image, imageCropping, noOverlay, mobileImage)}
				<div className={classNames(styles.HeroCard_textbox, { [styles.HeroCard_textbox__hasHeadingLink]: !!headingLink })}>
					<UI_Container width="Standard">
						<div className={styles.HeroCard_textboxInner}>
							{!hasImage ? (
								<UI_TopLevelGrid container nogutter wrap>
									{kickerExists && kickerLeft && (
										<UI_TopLevelGridCell desktopWidth="7col" mobileWidth="100">
											<UI_Kicker {...kicker} className={styles.HeroCard_kicker} />
										</UI_TopLevelGridCell>
									)}

									<UI_TopLevelGridCell
										mobileWidth="100"
										desktopWidth={kickerExists && kickerLeft ? '17col' : '17col'}
										desktopOffset={!kickerExists && kickerLeft ? '7col' : '0col'}
										offsetDirection="left"
									>
										{kickerExists && !kickerLeft && <UI_Kicker {...kicker} className={styles.HeroCard_kicker} />}
										<UI_Heading headingLevel="h1" style="xxl" className={classNames(styles.HeroCard_heading, 'HeroCard_heading')}>
											<span className={styles.HeroCard_kickerInner}>{heading}</span>
										</UI_Heading>
									</UI_TopLevelGridCell>
								</UI_TopLevelGrid>
							) : (
								<>
									{kickerExists && kickerLeft ? (
										<UI_TopLevelGrid container wrap>
											<UI_TopLevelGridCell desktopWidth="7col" mobileWidth="100">
												{<UI_Kicker {...kicker} className={styles.HeroCard_kicker} />}
											</UI_TopLevelGridCell>
											<UI_TopLevelGridCell desktopWidth="17col" mobileWidth="100">
												<UI_Heading
													headingLevel="h1"
													style="xxl"
													className={classNames(styles.HeroCard_heading, 'HeroCard_heading')}
												>
													<span className={styles.HeroCard_kickerInner}>{heading}</span>
												</UI_Heading>
											</UI_TopLevelGridCell>
										</UI_TopLevelGrid>
									) : (
										<UI_TopLevelGrid container wrap>
											<UI_TopLevelGridCell
												mobileWidth="100"
												desktopWidth={kickerExists && kickerLeft ? '10col' : '17col'}
												desktopOffset={!kickerExists && kickerLeft ? '7col' : '0col'}
												offsetDirection="left"
											>
												{kickerExists && <UI_Kicker {...kicker} className={styles.HeroCard_kicker} />}
												{(!headingLink || !noOverlay) && (
													<UI_Heading
														headingLevel="h1"
														style="xxl"
														className={classNames(styles.HeroCard_heading, 'HeroCard_heading')}
													>
														<span className={styles.HeroCard_kickerInner}>{heading}</span>
													</UI_Heading>
												)}

												{headingLink && !noOverlay && (
													<div className={styles.HeroCard_LinkContainer}>
														<UI_LinkButton
															href={headingLink?.url}
															className={styles.HeroCard_headingLink}
															style={linkButtonStyle}
															target={headingLink?.target}
															colorOverride={buttonColorOverride}
															colorOverrideTextColor={buttonColorOverrideTextColor}
														>
															{headingLink?.name}
														</UI_LinkButton>
													</div>
												)}
											</UI_TopLevelGridCell>
										</UI_TopLevelGrid>
									)}
								</>
							)}
						</div>
						{noOverlay && headingLink && (
							<div className={styles.HeroCard_textboxInner}>
								<UI_TopLevelGrid container wrap className={styles.HeroCard_headingRow}>
									<UI_TopLevelGridCell mobileWidth="100" desktopWidth="17col" className={styles.HeroCard_headingTextCell}>
										<UI_Heading headingLevel="h1" style="xxl" className={classNames(styles.HeroCard_heading, 'HeroCard_heading')}>
											<span className={styles.HeroCard_kickerInner}>{heading}</span>
										</UI_Heading>
									</UI_TopLevelGridCell>
									<UI_TopLevelGridCell mobileWidth="100" desktopWidth="7col" className={styles.HeroCard_headingLinkCell}>
										<div className={styles.HeroCard_headingLinkContainer}>
											<UI_LinkButton
												href={headingLink?.url}
												className={styles.HeroCard_headingLink}
												style={linkButtonStyle}
												target={headingLink?.target}
												colorOverride={buttonColorOverride}
												colorOverrideTextColor={buttonColorOverrideTextColor}
											>
												{headingLink?.name}
											</UI_LinkButton>
										</div>
									</UI_TopLevelGridCell>
								</UI_TopLevelGrid>
							</div>
						)}
					</UI_Container>
				</div>

				{(image?.url || mobileImage?.url) && noOverlay && (
					<UI_Container width="Standard">{renderImageOrVideo(video, image, imageCropping, noOverlay, mobileImage)}</UI_Container>
				)}
			</div>
			{text && (
				<UI_Container width="Standard">
					<UI_TopLevelGrid container wrap>
						{sideText || infoBox ? (
							<>
								<UI_TopLevelGridCell desktopWidth="7col" mobileWidth="100" className={styles.HeroCard_sideTextWrapper}>
									{sideText && (
										<div className={classNames(styles.HeroCard_sideText, !hasImage && styles.HeroCard_sideText___noImg)}>
											<UI_RichText
												content={sideText}
												readMoreButtonText={readMoreButtonSideText}
												readMoreText={readMoreSideText}
											/>
										</div>
									)}
									{infoBox && (
										<UI_InfoBox
											content={infoBox.content}
											color={infoBox.color}
											variant={infoBox.variant}
											className={classNames(styles.HeroCard_infoBox, !hasImage && styles.HeroCard_infoBox___noImg)}
										/>
									)}
								</UI_TopLevelGridCell>
								<UI_TopLevelGridCell
									desktopWidth={'10col'}
									expand="17col"
									mobileWidth="100"
									className={styles.HeroCard_manchetWrapper}
								>
									<div className={classNames(styles.HeroCard_manchet, !hasImage && styles.HeroCard_manchet___noImg)}>
										<UI_RichText content={text} readMoreButtonText={readMoreButtonText} readMoreText={readMoreText} bigText />
									</div>
								</UI_TopLevelGridCell>
							</>
						) : (
							<>
								<UI_TopLevelGridCell desktopWidth="10col" desktopOffset={'7col'} center mobileWidth="100">
									<div className={classNames(styles.HeroCard_manchet, !hasImage && styles.HeroCard_manchet___noImg)}>
										<UI_RichText content={text} readMoreButtonText={readMoreButtonText} readMoreText={readMoreText} bigText />
									</div>
								</UI_TopLevelGridCell>
							</>
						)}
					</UI_TopLevelGrid>
				</UI_Container>
			)}
		</header>
	);
};
